import Button, { ButtonVariant } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { TestQuestion as TestQuestionType } from "../types/test-detail.types";
import TextRenderer from "./TextRenderer";

interface TestQuestionProps {
  question: TestQuestionType;
  selectedOption?: string;
  onAnswer: (optionId: string) => Promise<void> | void;
  onHoverLineReference?: (
    start?: number,
    end?: number,
    questionNumber?: number
  ) => void;
  showCorrectAnswer?: boolean;
  correctOption?: string;
  hasPrevious?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  isLast?: boolean;
}

export default function TestQuestion({
  question,
  selectedOption,
  onAnswer,
  onHoverLineReference,
  showCorrectAnswer,
  correctOption,
  hasPrevious,
  onPrevious,
  onNext,
  isLast,
}: TestQuestionProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [localSelectedOption, setLocalSelectedOption] = useState<
    string | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create a ref for the question container
  const questionRef = useRef<HTMLDivElement>(null);

  // Update local selection when question or selectedOption changes
  useEffect(() => {
    setLocalSelectedOption(selectedOption);
  }, [question.id, selectedOption]);

  const handleMouseEnter = () => {
    setIsHovered(true);

    // Send line references if available
    if (question.lineReferences && onHoverLineReference) {
      onHoverLineReference(
        question.lineReferences.start,
        question.lineReferences.end,
        question.number // Send the question number
      );
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (onHoverLineReference) {
      onHoverLineReference();
    }
  };

  const getOptionClassName = (optionId: string) => {
    const baseClass =
      "flex items-center p-4 rounded-lg border transition-colors";
    const isSelected = showCorrectAnswer
      ? selectedOption === optionId
      : localSelectedOption === optionId;

    if (!showCorrectAnswer) {
      return `${baseClass} ${
        isSelected
          ? "border-yellow-500 bg-yellow-500 text-gray-700"
          : "border-gray-200 hover:border-gray-300"
      }`;
    }

    if (optionId === correctOption) {
      return `${baseClass} border-green-500 bg-green-50`;
    }

    if (isSelected && optionId !== correctOption) {
      return `${baseClass} border-red-500 bg-red-50`;
    }

    return `${baseClass} border-gray-200`;
  };

  const handleOptionSelect = (optionId: string) => {
    if (!showCorrectAnswer) {
      setLocalSelectedOption(optionId);
    }
  };

  const handleNext = async () => {
    if (!localSelectedOption) return;

    try {
      setIsSubmitting(true);

      // Call onAnswer and wait for it to complete
      await onAnswer(localSelectedOption);

      // If we get here, there was no error, so proceed to next question
      onNext?.();

      // Scroll to the top of the question container
      if (questionRef.current) {
        questionRef.current.scrollIntoView({
          behavior: "instant",
          block: "start",
        });
      }
    } catch (error) {
      // Show error toast
      toast.error("Failed to submit answer. Please try again.");
      console.error("Error submitting answer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`${theme.bg.primary} rounded-lg shadow md:p-6 p-4 mb-6`}
      ref={questionRef}
    >
      <div
        className={`mb-6 ${isHovered ? "cursor-pointer" : ""} flex`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className="font-medium mr-2">{question.number}.</span>
        <TextRenderer text={question.text} allowedAttr={["class"]} />
      </div>

      <div className="space-y-3 mb-6">
        {question.options?.length > 0 ? (
          // Multiple choice question
          question.options.map((option) => (
            <button
              key={option.id}
              className={getOptionClassName(option.id)}
              onClick={() => handleOptionSelect(option.id)}
              disabled={showCorrectAnswer}
            >
              <div className="flex-1 text-left flex">
                <span className="font-medium mr-2">{option.option}.</span>
                {option.text && (
                  <div>
                    <TextRenderer
                      text={option.text}
                      allowHtml={true}
                      allowedTags={[
                        "sup",
                        "sub",
                        "b",
                        "i",
                        "em",
                        "strong",
                        "span",
                      ]}
                      allowedAttr={["class"]}
                    />
                  </div>
                )}
              </div>
            </button>
          ))
        ) : (
          // Free text question
          <div className="w-full">
            <textarea
              value={localSelectedOption || ""}
              onChange={(e) => setLocalSelectedOption(e.target.value)}
              placeholder="Write your answer here..."
              className={`w-full min-h-[100px] p-3 border border-gray-200 rounded-lg focus: ${theme.border.brand} focus:ring-1 focus:ring-violet-500 outline-none resize-y`}
              disabled={showCorrectAnswer}
            />
          </div>
        )}
      </div>

      {!showCorrectAnswer && (
        <div className="flex justify-between mt-6">
          <Button
            onClick={onPrevious}
            disabled={!hasPrevious || isSubmitting}
            variant={ButtonVariant.TERTIARY}
          >
            Previous
          </Button>
          <Button
            onClick={handleNext}
            disabled={!localSelectedOption || isSubmitting}
            loadingText={isLast ? "Finishing..." : "Saving..."}
            isLoading={isSubmitting}
          >
            {isLast ? "Finish" : "Next"}
          </Button>
        </div>
      )}
    </div>
  );
}
